import { useState, useMemo, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCachedQuery, useCachedMutations } from '@/hooks/useCachedQuery'
import { useApi } from '@/hooks/useApi'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import type { PagoRecurrente, Cliente, FrecuenciaPago } from '@/lib/types'
import {
  FRECUENCIAS_PAGO, FRECUENCIA_LABEL, USA_DIA_DEL_MES, USA_DIA_DE_SEMANA, DIAS_SEMANA_ISO,
} from '@/lib/types'
import { formatCurrency, formatDate } from '@/lib/utils'
import { IconArrowDown, IconArrowUp, IconCalendar, IconCheckCircle, IconCircleFilled, IconEdit, IconPlus, IconSearch, IconTrash } from '@/components/icons/Icons'
import { KpiRow, KpiStrip, PageHeader, Pagination, StatCard } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'

const frecuenciaLabel = FRECUENCIA_LABEL

const CRUMBS = [{ label: 'Contabilidad', to: '/contabilidad' }, { label: 'Pagos Recurrentes' }]

/* Normaliza cualquier frecuencia a su equivalente mensual: el dato util no es
   "S/ 906 al ano" sino cuanto compromete el mes. */
const vecesPorMes: Record<FrecuenciaPago, number> = {
  diaria: 30, semanal: 52 / 12, bisemanal: 26 / 12, quincenal: 2, mensual: 1,
  bimestral: 1 / 2, trimestral: 1 / 3, semestral: 1 / 6, anual: 1 / 12,
}

/* Cómo se lee `dia_ejecucion` en la tabla, que cambia de significado según la
   frecuencia. */
function diaLegible(r: PagoRecurrente): string | null {
  if (r.dia_ejecucion == null) return null
  if (USA_DIA_DEL_MES.includes(r.frecuencia)) return `Día ${r.dia_ejecucion}`
  if (USA_DIA_DE_SEMANA.includes(r.frecuencia)) {
    return DIAS_SEMANA_ISO.find(d => d.valor === r.dia_ejecucion)?.label ?? null
  }
  return null
}

const emptyRec: Omit<PagoRecurrente, 'id' | 'created_at'> = {
  tipo: 'egreso', concepto: '', descripcion: '', monto: 0, moneda: 'PEN',
  frecuencia: 'mensual', dia_ejecucion: new Date().getDate(),
  fecha_inicio: new Date().toISOString().slice(0, 10),
  fecha_fin: null, activo: true, cliente_id: null, notas: null,
}

export default function PagosRecurrentes() {
  const { data: items = [], isLoading: loading } = useCachedQuery<PagoRecurrente>('pagos_recurrentes')
  const { addMut, updateMut, removeMut } = useCachedMutations('pagos_recurrentes')
  const { data: clientes = [] } = useCachedQuery<Cliente>('clientes')
  const api = useApi()
  const qc = useQueryClient()

  const [modal, setModal] = useState<'create' | 'edit' | 'generate' | null>(null)
  const [form, setForm] = useState(emptyRec)
  const [editId, setEditId] = useState<number | null>(null)
  const [del, setDel] = useState<PagoRecurrente | null>(null)
  const [search, setSearch] = useState('')
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [filtroEstado, setFiltroEstado] = useState('todos')
  const [filtroFrecuencia, setFiltroFrecuencia] = useState('todos')
  const [genTarget, setGenTarget] = useState<PagoRecurrente | null>(null)
  const [genMonths, setGenMonths] = useState(3)
  const [genPreview, setGenPreview] = useState<string[]>([])
  const [generando, setGenerando] = useState(false)
  const [previewDates, setPreviewDates] = useState<string[]>([])

  /* ─── Preview de próximas fechas ──────────────────────────────────────────
     Lo calcula el backend (`api/src/lib/recurrencia.ts`). Antes se calculaba
     acá sumando días fijos —mensual = 30 días—, así que el preview y lo que
     después se generaba podían no coincidir, y ninguna serie caía dos meses
     seguidos en el mismo día. */
  // `useApi()` devuelve un objeto nuevo en cada render: como dependencia de un
  // efecto que hace setState, es un bucle infinito. Las funciones que devuelve
  // sí son estables, así que se depende de la función, no del objeto.
  const apiPost = api.post

  useEffect(() => {
    if (!form.fecha_inicio || !form.frecuencia) {
      setPreviewDates(p => (p.length ? [] : p))
      return
    }
    let vigente = true
    const t = setTimeout(async () => {
      try {
        const r = await apiPost<{ fechas: string[] }>('/api/contabilidad/recurrencia/preview', {
          frecuencia: form.frecuencia,
          fecha_inicio: form.fecha_inicio,
          dia_ejecucion: form.dia_ejecucion,
          fecha_fin: form.fecha_fin,
          repeticiones: 3,
        })
        if (vigente) setPreviewDates(r.fechas)
      } catch {
        if (vigente) setPreviewDates([])
      }
    }, 250)
    return () => { vigente = false; clearTimeout(t) }
  }, [form.fecha_inicio, form.frecuencia, form.dia_ejecucion, form.fecha_fin, apiPost])

  /* Cuántos vencimientos salen de los meses pedidos, sin inventarlo en el
     navegador: el mismo cálculo que hará el POST. */
  useEffect(() => {
    if (!genTarget?.fecha_inicio) {
      setGenPreview(p => (p.length ? [] : p))
      return
    }
    let vigente = true
    const t = setTimeout(async () => {
      try {
        const r = await apiPost<{ fechas: string[] }>('/api/contabilidad/recurrencia/preview', {
          frecuencia: genTarget.frecuencia,
          fecha_inicio: genTarget.fecha_inicio,
          dia_ejecucion: genTarget.dia_ejecucion,
          fecha_fin: genTarget.fecha_fin,
          repeticiones: Math.max(1, Math.min(120, Math.round(vecesPorMes[genTarget.frecuencia] * genMonths))),
        })
        if (vigente) setGenPreview(r.fechas)
      } catch {
        if (vigente) setGenPreview([])
      }
    }, 200)
    return () => { vigente = false; clearTimeout(t) }
  }, [genTarget, genMonths, apiPost])

  /* ─── Filtrar ─── */
  const filtered = useMemo(() => {
    let result = items
    if (filtroTipo !== 'todos') result = result.filter(r => r.tipo === filtroTipo)
    if (filtroEstado !== 'todos') result = result.filter(r => (filtroEstado === 'activo' ? r.activo : !r.activo))
    if (filtroFrecuencia !== 'todos') result = result.filter(r => r.frecuencia === filtroFrecuencia)
    if (search) {
      const q = search.toLowerCase()
      // El proveedor vive en `concepto`; buscar solo por descripcion no
      // encontraba "Anthropic" aunque estuviera en pantalla.
      result = result.filter(r =>
        r.concepto?.toLowerCase().includes(q) ||
        r.descripcion?.toLowerCase().includes(q) ||
        r.notas?.toLowerCase().includes(q)
      )
    }
    return result
  }, [items, search, filtroTipo, filtroEstado, filtroFrecuencia])

  const { page, setPage, totalPages, paginated, pageSize, total } =
    usePagination(filtered, 15, `${search}|${filtroTipo}|${filtroEstado}|${filtroFrecuencia}`)

  const hayFiltros = !!search || filtroTipo !== 'todos' || filtroEstado !== 'todos' || filtroFrecuencia !== 'todos'
  const limpiarFiltros = () => {
    setSearch(''); setFiltroTipo('todos'); setFiltroEstado('todos'); setFiltroFrecuencia('todos')
  }

  /* ─── Compromiso mensual ────────────────────────────────────────────────
     Lo que importa de un recurrente no es su monto sino lo que compromete
     cada mes; un anual de S/ 10 880 no es un gasto de S/ 10 880 mensuales.
     Solo cuentan los activos, y nunca se suman monedas distintas. */
  const compromiso = useMemo(() => {
    const activos = items.filter(r => r.activo)
    const monedas = [...new Set(activos.map(r => r.moneda || 'PEN'))]
    return monedas.map(moneda => {
      const grupo = activos.filter(r => (r.moneda || 'PEN') === moneda)
      const suma = (tipo: string) => grupo
        .filter(r => r.tipo === tipo)
        .reduce((acc, r) => acc + Number(r.monto || 0) * (vecesPorMes[r.frecuencia] ?? 1), 0)
      return { moneda, egreso: Math.round(suma('egreso') * 100) / 100, ingreso: Math.round(suma('ingreso') * 100) / 100 }
    }).filter(t => t.egreso || t.ingreso)
  }, [items])

  /* ─── CRUD ─── */
  const openCreate = () => {
    setForm({ ...emptyRec, fecha_inicio: new Date().toISOString().slice(0, 10) })
    setEditId(null)
    setModal('create')
  }
  const openEdit = (r: PagoRecurrente) => {
    setForm({
      tipo: r.tipo, concepto: r.concepto ?? '', descripcion: r.descripcion, monto: r.monto, moneda: r.moneda,
      frecuencia: r.frecuencia, dia_ejecucion: r.dia_ejecucion,
      fecha_inicio: r.fecha_inicio, fecha_fin: r.fecha_fin,
      activo: r.activo, cliente_id: r.cliente_id, notas: r.notas,
    })
    setEditId(r.id)
    setModal('edit')
  }

  const handleSave = async () => {
    if (!(form.concepto ?? '').trim()) { showToast('Concepto requerido', 'error'); return }
    if (form.monto <= 0) { showToast('Monto debe ser mayor a 0', 'error'); return }
    try {
      if (modal === 'edit' && editId) {
        await updateMut.mutateAsync({ id: editId, ...form } as never)
        showToast('Pago recurrente actualizado', 'success')
      } else {
        await addMut.mutateAsync(form as never)
        showToast('Pago recurrente creado', 'success')
      }
      setModal(null)
    } catch {
      showToast('Error al guardar', 'error')
    }
  }

  const handleDelete = async () => {
    if (!del) return
    try {
      await removeMut.mutateAsync(del.id)
      showToast('Pago recurrente eliminado', 'success')
      setDel(null)
    } catch {
      showToast('Error al eliminar', 'error')
    }
  }

  /* ─── Toggle activo ─── */
  const toggleActivo = async (r: PagoRecurrente) => {
    try {
      await updateMut.mutateAsync({ id: r.id, activo: !r.activo } as never)
      showToast(r.activo ? 'Pago desactivado' : 'Pago activado', 'success')
    } catch {
      showToast('Error al cambiar estado', 'error')
    }
  }

  /* ─── Generar proyecciones ─── */
  const openGenerate = (r: PagoRecurrente) => {
    setGenTarget(r)
    setGenMonths(3)
    setModal('generate')
  }

  /* El backend genera e inserta en una sola llamada, con ON CONFLICT DO
     NOTHING sobre (pago_recurrente_id, fecha_vencimiento): pulsar «Generar»
     dos veces ya no duplica la serie, solo informa lo que ya estaba. */
  const handleGenerate = async () => {
    if (!genTarget) return
    if (!genTarget.fecha_inicio) {
      showToast('Este recurrente no tiene fecha de inicio: editalo y asignale una', 'error')
      return
    }
    setGenerando(true)
    try {
      const r = await api.post<{ creados: number; existentes: number }>(
        `/api/contabilidad/pagos-recurrentes/${genTarget.id}/generar`,
        { meses: genMonths },
      )
      qc.invalidateQueries({ queryKey: ['pagos_programados'] })
      showToast(
        r.existentes > 0
          ? `${r.creados} proyecciones nuevas — ${r.existentes} ya estaban`
          : `${r.creados} proyecciones generadas`,
        'success',
      )
      setModal(null)
      setGenTarget(null)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Error al generar proyecciones', 'error')
    } finally {
      setGenerando(false)
    }
  }

  if (loading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}>
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...
      </div>
    </div>
  )

  return (
    <div className="page-enter page-shell" id="pagos-recurrentes">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === items.length
          ? `${items.length} definici${items.length !== 1 ? 'ones' : 'ón'}`
          : `${total} de ${items.length}`}
        actions={
          <button className="btn btn-primary" onClick={openCreate} id="btn-new-pago-recurrente">
            <IconPlus size={16} /> Nuevo Recurrente
          </button>
        }
      />

      {compromiso.length > 0 && (
        <KpiStrip>
          {compromiso.map(t => (
            <KpiRow moneda={t.moneda} key={t.moneda}>
              {t.egreso > 0 && (
                <StatCard label="Egreso mensual" value={formatCurrency(t.egreso, t.moneda)} tone="warning" />
              )}
              {t.ingreso > 0 && (
                <StatCard label="Ingreso mensual" value={formatCurrency(t.ingreso, t.moneda)} tone="success" />
              )}
            </KpiRow>
          ))}
        </KpiStrip>
      )}

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input className="form-input" placeholder="Buscar por concepto, descripción o nota..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{ width: 145 }}>
          <option value="todos">Activos e inactivos</option>
          <option value="activo">Solo activos</option>
          <option value="inactivo">Solo inactivos</option>
        </select>
        <select className="form-input" value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{ width: 150 }}>
          <option value="todos">Ingresos y egresos</option>
          <option value="egreso">Solo egresos</option>
          <option value="ingreso">Solo ingresos</option>
        </select>
        <select className="form-input" value={filtroFrecuencia} onChange={e => setFiltroFrecuencia(e.target.value)} style={{ width: 140 }}>
          <option value="todos">Toda frecuencia</option>
          {FRECUENCIAS_PAGO.map(f => <option key={f} value={f}>{frecuenciaLabel[f]}</option>)}
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={limpiarFiltros}>Limpiar filtros</button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <h3>Sin pagos recurrentes</h3>
          <p>Define pagos que se repiten periódicamente.</p>
          <button className="btn btn-primary" onClick={openCreate}>Crear Recurrente</button>
        </div>
      ) : (
        <>
        <div className="table-container page-shell__table">
          <table>
            <thead>
              <tr>
                <th>Descripción</th>
                <th>Tipo</th>
                <th>Monto</th>
                <th>Frecuencia</th>
                <th>Inicio</th>
                <th>Fin</th>
                <th>Estado</th>
                <th style={{ width: 190 }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                    Sin resultados para el filtro aplicado
                  </td>
                </tr>
              ) : paginated.map(r => (
                <tr key={r.id} className={r.activo ? undefined : 'row--dimmed'}>
                  <td>
                    {/* `concepto` trae el proveedor ("Anthropic, PBC - Max plan");
                        mostrar solo `descripcion` lo dejaba invisible. */}
                    <div className="cell-stack">
                      <span className="cell-stack__title">{r.concepto || r.descripcion || '—'}</span>
                      {r.concepto && r.descripcion && r.descripcion !== r.concepto && (
                        <span className="cell-stack__sub">{r.descripcion}</span>
                      )}
                      {r.cliente_id && (
                        <span className="cell-stack__sub">{clientes.find(c => c.id === r.cliente_id)?.empresa}</span>
                      )}
                      {!r.fecha_inicio && (
                        <span className="badge badge-rechazada" title="Sin fecha de inicio no se pueden generar proyecciones">Sin inicio</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${r.tipo === 'ingreso' ? 'badge-ingreso' : 'badge-egreso'}`}>
                      {r.tipo === 'ingreso' ? <IconArrowUp size={11} /> : <IconArrowDown size={11} />}
                      {r.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                    </span>
                  </td>
                  <td className="cell-amount">{formatCurrency(r.monto, r.moneda)}</td>
                  <td>
                    <div className="cell-stack">
                      <span className="badge badge-recurrente">{frecuenciaLabel[r.frecuencia] || r.frecuencia}</span>
                      {diaLegible(r) && <span className="cell-stack__sub">{diaLegible(r)}</span>}
                    </div>
                  </td>
                  <td className="cell-date">{formatDate(r.fecha_inicio)}</td>
                  <td className="cell-date">{r.fecha_fin ? formatDate(r.fecha_fin) : 'Sin fin'}</td>
                  <td>
                    <button
                      className={`btn btn-sm ${r.activo ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => toggleActivo(r)}
                      title={r.activo ? 'Desactivar' : 'Activar'}
                      style={{ fontSize: '0.72rem', padding: '4px 8px', gap: 4 }}
                    >
                      {r.activo ? <IconCheckCircle size={12} /> : <IconCircleFilled size={8} />}
                      {r.activo ? 'Activo' : 'Inactivo'}
                    </button>
                  </td>
                  <td>
                    <div className="row-actions row-actions--nowrap">
                      <button className="btn btn-sm btn-secondary" onClick={() => openGenerate(r)} title="Generar proyecciones">
                        <IconCalendar size={13} /> Generar
                      </button>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(r)} title="Editar"><IconEdit size={13} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => setDel(r)} title="Eliminar"><IconTrash size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize}
          onPage={setPage} noun="recurrentes" />
        </>
      )}

      {/* ─── Modal Crear/Editar ─── */}
      <Modal isOpen={modal === 'create' || modal === 'edit'} onClose={() => setModal(null)} title={modal === 'edit' ? 'Editar Recurrente' : 'Nuevo Pago Recurrente'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Tipo</label>
              <select className="form-input" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value as 'ingreso' | 'egreso' }))}>
                <option value="egreso">Egreso</option>
                <option value="ingreso">Ingreso</option>
              </select>
            </div>
            <div>
              <label className="form-label">Frecuencia *</label>
              <select
                className="form-input"
                value={form.frecuencia}
                onChange={e => {
                  const frecuencia = e.target.value as FrecuenciaPago
                  // El día pedido cambia de significado según la frecuencia:
                  // arrastrar un "20" a una semanal daría un día inexistente.
                  setForm(f => ({
                    ...f,
                    frecuencia,
                    dia_ejecucion: USA_DIA_DEL_MES.includes(frecuencia)
                      ? (f.dia_ejecucion && f.dia_ejecucion <= 31 ? f.dia_ejecucion : Number(f.fecha_inicio.slice(8, 10)) || 1)
                      : USA_DIA_DE_SEMANA.includes(frecuencia) ? 1 : null,
                  }))
                }}
              >
                {FRECUENCIAS_PAGO.map(f => <option key={f} value={f}>{frecuenciaLabel[f]}</option>)}
              </select>
            </div>
          </div>

          {/* `dia_ejecucion` estaba en la base (30, 20, 13…) sin que nada lo
              leyera: el generador sumaba días fijos. Ahora es el que manda. */}
          {(USA_DIA_DEL_MES.includes(form.frecuencia) || USA_DIA_DE_SEMANA.includes(form.frecuencia)) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {USA_DIA_DEL_MES.includes(form.frecuencia) ? (
                <div>
                  <label className="form-label">Día del mes</label>
                  <input
                    type="number" className="form-input" min={1} max={31}
                    value={form.dia_ejecucion ?? ''}
                    onChange={e => setForm(f => ({
                      ...f, dia_ejecucion: e.target.value ? parseInt(e.target.value) : null,
                    }))}
                  />
                  <span className="form-hint">Si el mes no tiene ese día, vence el último</span>
                </div>
              ) : (
                <div>
                  <label className="form-label">Día de la semana</label>
                  <select
                    className="form-input"
                    value={form.dia_ejecucion ?? 1}
                    onChange={e => setForm(f => ({ ...f, dia_ejecucion: parseInt(e.target.value) }))}
                  >
                    {DIAS_SEMANA_ISO.map(d => <option key={d.valor} value={d.valor}>{d.label}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
          <div>
            <label className="form-label">Concepto *</label>
            <input className="form-input" value={form.concepto ?? ''} onChange={e => setForm(f => ({ ...f, concepto: e.target.value }))} placeholder="Ej: Anthropic, PBC — Max plan, Alquiler oficina..." />
          </div>
          <div className="form-group">
            <label className="form-label">Descripción</label>
            <input className="form-input" value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} placeholder="Detalle opcional que se muestra bajo el concepto" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Monto por período *</label>
              <input type="number" className="form-input" value={form.monto || ''} onChange={e => setForm(f => ({ ...f, monto: parseFloat(e.target.value) || 0 }))} min="0" step="0.01" />
            </div>
            <div>
              <label className="form-label">Moneda</label>
              <select className="form-input" value={form.moneda} onChange={e => setForm(f => ({ ...f, moneda: e.target.value }))}>
                <option value="PEN">PEN</option><option value="USD">USD</option><option value="EUR">EUR</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Fecha de inicio *</label>
              <input type="date" className="form-input" value={form.fecha_inicio} onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Fecha fin (opcional)</label>
              <input type="date" className="form-input" value={form.fecha_fin || ''} onChange={e => setForm(f => ({ ...f, fecha_fin: e.target.value || null }))} />
            </div>
          </div>
          <div>
            <label className="form-label">Cliente (opcional)</label>
            <select className="form-input" value={form.cliente_id ?? ''} onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value ? parseInt(e.target.value) : null }))}>
              <option value="">— Ninguno —</option>
              {clientes.map(c => <option key={c.id} value={c.id}>{c.empresa}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Notas</label>
            <textarea className="form-input" value={form.notas || ''} onChange={e => setForm(f => ({ ...f, notas: e.target.value || null }))} rows={2} />
          </div>

          {/* Preview próximas fechas */}
          {previewDates.length > 0 && (
            <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: 4, color: 'var(--text-secondary)' }}>
                Próximas fechas calculadas:
              </div>
              {previewDates.map((d, i) => (
                <div key={i} style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                  {i + 1}. {formatDate(d)}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={addMut.isPending || updateMut.isPending}>
            {(addMut.isPending || updateMut.isPending) ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </Modal>

      {/* ─── Modal Generar Proyecciones ─── */}
      <Modal isOpen={modal === 'generate'} onClose={() => { setModal(null); setGenTarget(null) }} title="Generar Proyecciones">
        {genTarget && (
          <div>
            <p>Generar pagos programados futuros para <strong>{genTarget.descripcion}</strong></p>
            <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 12, margin: '12px 0' }}>
              <div>{formatCurrency(genTarget.monto, genTarget.moneda)} — {frecuenciaLabel[genTarget.frecuencia]}</div>
            </div>
            <div>
              <label className="form-label">¿Cuántos meses hacia adelante?</label>
              <input type="number" className="form-input" value={genMonths} onChange={e => setGenMonths(parseInt(e.target.value) || 1)} min="1" max="24" style={{ width: 100 }} />
            </div>
            {/* Antes decía "hasta {meses × 4} pagos": con una serie mensual,
                pedir 3 meses creaba 12 proyecciones. Ahora el conteo sale del
                mismo cálculo que hará el POST. */}
            <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              {genPreview.length > 0
                ? <>Se crearán {genPreview.length} vencimiento{genPreview.length !== 1 ? 's' : ''}, hasta el {formatDate(genPreview[genPreview.length - 1])}.</>
                : 'Calculando vencimientos...'}
            </div>
            {genPreview.length > 0 && (
              <div className="repeticion__preview-list" style={{ marginTop: 8 }}>
                {genPreview.slice(0, 6).map(d => (
                  <span key={d} className="badge badge-recurrente">{formatDate(d)}</span>
                ))}
                {genPreview.length > 6 && (
                  <span className="repeticion__preview-more">+{genPreview.length - 6} más</span>
                )}
              </div>
            )}
            <div className="form-hint" style={{ marginTop: 10 }}>
              Los vencimientos que ya existan no se duplican.
            </div>
          </div>
        )}
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => { setModal(null); setGenTarget(null) }}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGenerate} disabled={generando}>
            <IconCalendar size={14} /> {generando ? 'Generando...' : 'Generar'}
          </button>
        </div>
      </Modal>

      {/* ─── Modal Eliminar ─── */}
      <Modal isOpen={!!del} onClose={() => setDel(null)} title="Eliminar Pago Recurrente">
        <p>¿Eliminar <strong>{del?.descripcion}</strong>?</p>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Las proyecciones ya generadas no se eliminarán.</p>
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleDelete}>Eliminar</button>
        </div>
      </Modal>
    </div>
  )
}
